import {SelectionModel} from '@angular/cdk/collections';
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {
  PricingPeriodDialogComponent
} from '@features/leasing/lease-view/lease-rates-and-charges/pricing-period-dialog/pricing-period-dialog.component';
import {LeasingService} from "@features/leasing/leasing.service";
import {PanelType} from "@shared-components/info-panel/info-panel.component";
import {DateUtilsService} from '@shared-services/dateUtils.service';
import {UtilsService} from "@shared-services/utils.service";
import {LoadingService} from "projects/nter-ui-kit/src/lib/framework/util/service/loading.service";
import {LeasePricingPeriod, LeaseSummary, OccupancyType, PaymentMethod, RentalPricingOption} from "../../leasing.model";
import {LeaseViewService} from "../lease-view.service";

@Component({
  selector: 'app-lease-rates-and-charges',
  templateUrl: './lease-rates-and-charges.component.html',
})
export class LeaseRatesAndChargesComponent implements OnInit {
  @Input() id!: string;
  @Output() reloadLease = new EventEmitter<void>();

  leasePricingPeriod!: LeasePricingPeriod;
  leasePricingPeriodOld!: LeasePricingPeriod;
  leaseSummary: LeaseSummary | null = null;
  paymentMethods: PaymentMethod[] = [];
  isAddingTransactionSource: boolean = false;
  leasePricingPeriodDataSource: MatTableDataSource<LeasePricingPeriod> = new MatTableDataSource();
  pricingPeriodDisplayedColumns: string[] = ['select', 'uuid', 'rentalPricingOption', 'fixedRentalPricingValue', 'rentalPricingTurnoverPercentageValue', 'applyTransactionFee', 'startDate', 'endDate']
  pricingPeriodSelection = new SelectionModel<LeasePricingPeriod>();
  selectedPricingPeriodUuid: string | null = null;


  constructor(public utils: UtilsService,
              private leaseViewService: LeaseViewService,
              public loader: LoadingService,
              public dateUtils: DateUtilsService,
              private matDialog: MatDialog,
              private leasingService: LeasingService) {
  }

  ngOnInit() {
    this.loadPaymentMethods();
    this.loadLeasePricingPeriods();
    this.loadLease();
  }

  loadLease(): void {
    this.leaseViewService.getOne('', this.id).subscribe(
      {
        next: value => {
          this.leaseSummary = value;
        },
        error: error => {
          console.log(error);
        }
      }
    )
  }
  loadLeasePricingPeriods() {
    this.leaseViewService.getAllLeasePricingPeriods(this.id).subscribe({
      next: value => {
        this.leasePricingPeriodDataSource.data = value
      },
      error: err => {
        console.log(err);
      }
    });
  }

  loadLeaseTransactionSource() {
    this.leaseViewService.getLeaseCurrentPricingPeriod(this.id).subscribe({
      next: value => {
        this.leasePricingPeriodOld =  {...value};
        this.leasePricingPeriod =  {...value};
      },
      error: err => console.log(err)
    })
  }

  loadPaymentMethods() {
    this.leaseViewService.getPaymentMethods(this.id).subscribe({
      next: value => {
        this.paymentMethods = value;
      },
      error: err => console.log(err)
    })
  }

  displayDate(date: any): string {
    if (typeof date === 'number') {
      let dateUnix: string | null = this.dateUtils.displayShortDateUnix(date);
      return dateUnix ? dateUnix : '';
    } else if (date instanceof Date) {
      let shortDate: string | null = this.dateUtils.displayShortDate(date);
      return shortDate ? shortDate: '';
    }
    return '';
  }


  cancel() {
    this.leasePricingPeriod = {...this.leasePricingPeriodOld};
    this.isAddingTransactionSource = false;
  }

  protected readonly PanelType = PanelType;
  protected readonly OccupancyType = OccupancyType;

  setSelectedPricingPeriod(row: LeasePricingPeriod): void {
    this.selectedPricingPeriodUuid = row.uuid;
    this.pricingPeriodSelection.toggle(row);

    if (this.pricingPeriodSelection.selected.length == 0) this.selectedPricingPeriodUuid = null;
  }

  removePricingPeriod(): void {
    if (this.selectedPricingPeriodUuid != null) {
      this.leaseViewService.removePricingPeriod(this.id, this.selectedPricingPeriodUuid!).subscribe({
        next: () => {
          this.pricingPeriodSelection.clear();

          const indexToDelete = this.leasePricingPeriodDataSource.data.findIndex(item => item.uuid === this.selectedPricingPeriodUuid);

          if (indexToDelete !== -1) {
            this.leasePricingPeriodDataSource.data.splice(indexToDelete, 1);
          } else {
            console.error(`Unable to remove item id ${this.selectedPricingPeriodUuid}`);
          }

          this.selectedPricingPeriodUuid = null;

        },
        error: err => {
          console.log(err);
        }
      });
    }

  }

  addPricingPeriod(): void {
    this.matDialog.open(PricingPeriodDialogComponent, {
      panelClass: 'dialog-xl',
      data: {
        isEditing: false,
        header: 'Configure a new pricing period',
        allPeriods: this.leasePricingPeriodDataSource.data,
        selectedPeriod: null,
        leaseId: this.id,
        leaseStartDate: this.leaseSummary?.leaseStartDate,
        leaseEndDate: this.leaseSummary?.leaseEndDate,
        paymentMethods: this.paymentMethods,
      }
    }).afterClosed().subscribe((response: {save: boolean, periodToSave: LeasePricingPeriod}) => {
      if (response.save) {

        this.leaseViewService.createNewLeasePricingPeriod( this.id, response.periodToSave).subscribe({
          next: () => {
            this.loadLeasePricingPeriods();
            this.reloadLease.emit();
          },
          error: err => console.log(err)
        });
      }
    });
  }

  editPricingPeriod(): void {
    this.matDialog.open(PricingPeriodDialogComponent, {
      panelClass: 'dialog-xl',
      data: {
        isEditing: true,
        header: 'Edit an existing pricing period',
        allPeriods: this.leasePricingPeriodDataSource.data,
        selectedPeriod: this.pricingPeriodSelection.selected[0],
        leaseId: this.id,
        leaseStartDate: this.leaseSummary?.leaseStartDate,
        leaseEndDate: this.leaseSummary?.leaseEndDate,
        paymentMethods: this.paymentMethods,
      }
    }).afterClosed().subscribe((response: {save: boolean, periodToSave: LeasePricingPeriod}) => {
      if (response.save) {
        this.leaseViewService.updateLeasePricingPeriod(this.id, response.periodToSave).subscribe({
          next: () => {
            this.loadLeasePricingPeriods();
            this.reloadLease.emit();
          },
          error: err => console.log(err)
        });
      }
    });
  }

  getRentalType(typeCode: RentalPricingOption | null): string {
    return this.leasingService.getRentalType(typeCode, this.leaseSummary!.baseBookingConfig ?? null);
  }
}
